import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../translate';
import PriceProp from '../../propTypes/priceProp';
import ImageProp from '../../propTypes/imageProp';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import AddParticipantBtn from './AddParticipantBtn';
import FormInput from '../../forms/FormInput';
import CloseIcon from '../../icons/CloseIcon';

function AcademyPaymentTabParticipants(props) {
  const {
    t,
    targetUsers,
    onTargetEmailsChange,
    errors,
    currentUserBoughtSession,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  const { email = '' } = loggedUser || {};

  const handleRemoveParticipant = indexToDelete => {
    onTargetEmailsChange(prevParticipants => {
      const emailParticipants = [
        ...prevParticipants.slice(0, indexToDelete),
        ...prevParticipants.slice(indexToDelete + 1),
      ];
      if (emailParticipants.length === 0) {
        if (currentUserBoughtSession) {
          return [
            {
              email: '',
              firstName: '',
              lastName: '',
            },
          ];
        }
        return [];
      }
      return [
        ...prevParticipants.slice(0, indexToDelete),
        ...prevParticipants.slice(indexToDelete + 1),
      ];
    });
  };

  const addParticipant = () => {
    onTargetEmailsChange(prevState => [
      ...prevState,
      {
        email: '',
        firstName: '',
        lastName: '',
      },
    ]);
  };

  const handleChange = useCallback(
    (index, field) => value => {
      onTargetEmailsChange(prevParticipants => {
        const newParticipants = [...prevParticipants];
        newParticipants[index][field] = value;
        return newParticipants;
      });
    },
    [onTargetEmailsChange],
  );

  return (
    <div id="academyParticipantsTab" className="row">
      <div className="academy-payment-participants">
        <p className="academy-participants-subheader">
          {t('academy.payment.emails.confirm')}
        </p>
        {targetUsers.map((participant, index) => (
          <div className="row academy-payment-email-container">
            <div className="col-lg-8 col-9">
              <FormInput
                id={`${participant.email}-${index}`}
                name={`${participant.email}-${index}`}
                value={
                  participant.email === email
                    ? `${t('you')} (${participant.email})`
                    : participant.email || ''
                }
                onChange={handleChange(index, 'email')}
                label={t('email')}
                labelClassName="col-lg-2 col-12 text-right"
                containerClassName="col-lg-10 col-12 mobile-padding"
                placeholder={t('academy.payment.emails.other')}
                groupProps={{ className: 'row' }}
                inputProps={{ autoComplete: 'off' }}
                disabled={participant.email === email}
                hint={
                  targetUsers?.length > 1 && participant.email === email
                    ? t('academy.enroll.participant.me.warning')
                    : undefined
                }
                hintClassName="participant-hint-warning"
              />

              {participant.email !== email && (
                <div>
                  <FormInput
                    id={`${participant.email}-${index}-firstName`}
                    name={`${participant.email}-${index}-firstName`}
                    value={participant.firstName || ''}
                    onChange={handleChange(index, 'firstName')}
                    label={t('name_holder')}
                    labelClassName="col-lg-3 col-12 text-right"
                    containerClassName="col-lg-9 col-12 mobile-padding"
                    placeholder={t('name_holder')}
                    groupProps={{ className: 'row' }}
                    inputProps={{ autoComplete: 'off' }}
                  />
                  <FormInput
                    id={`${participant.email}-${index}-lastName`}
                    name={`${participant.email}-${index}-lastName`}
                    value={participant.lastName || ''}
                    onChange={handleChange(index, 'lastName')}
                    label={t('lastName_holder')}
                    labelClassName="col-lg-3 col-12 text-right"
                    containerClassName="col-lg-9 col-12 mobile-padding"
                    placeholder={t('lastName_holder')}
                    groupProps={{ className: 'row' }}
                    inputProps={{ autoComplete: 'off' }}
                  />
                </div>
              )}

              {errors?.duplicatedEmails?.find(item =>
                item?.indices.includes(index),
              ) !== undefined && (
                <span className="has-error">{t('error.duplicated.email')}</span>
              )}

              {errors?.targetUsers?.find(i => i === index) >= 0 && (
                <span className="has-error">{t('error.valid.email')}</span>
              )}
              {errors?.emailsAlreadyInSession?.find(
                item => item?.email?.trim() === participant?.email?.trim(),
              ) !== undefined && (
                <span className="has-error">
                  {t('error.alreadyInvited.email')}
                </span>
              )}
            </div>
            <div className="col-lg-2 col-3 remove-email-btn">
              {targetUsers.length !== 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveParticipant(index)}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          </div>
        ))}
        <div>
          <AddParticipantBtn onClick={addParticipant} />
        </div>
      </div>
    </div>
  );
}

AcademyPaymentTabParticipants.propTypes = {
  t: PropTypes.func.isRequired,
  checkoutDetail: PropTypes.shape({
    isPaidWithCoins: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    item: {
      tax: PropTypes.number,
      total: PropTypes.number,
      price: PropTypes.number,
    },
  }).isRequired,
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    userPrice: PropTypes.shape(PriceProp.PropTypes),
    userPromotionPrice: PropTypes.shape(PriceProp.PropTypes),
    name: PropTypes.string.isRequired,
    image: PropTypes.shape(ImageProp.propType).isRequired,
  }).isRequired,
  targetUsers: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    }),
  ),
  onTargetEmailsChange: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.number),
  currentUserBoughtSession: PropTypes.bool.isRequired,
};
AcademyPaymentTabParticipants.defaultProps = {
  targetUsers: null,
  errors: [],
};

export default translate(AcademyPaymentTabParticipants);
